import React, { Fragment, useCallback, useMemo, useState } from 'react'
import { api } from '../api'
import { calculateDemoSemiFinishedTotalCost, isSemiFinishedEligibleForRecipeReport, formatQuantity } from '../costUtils'
import {
  Category,
  DemoComponentItem,
  DemoComponentType,
  DemoProduct,
  DemoSemiFinishedProduct,
  Packaging,
  Product,
  RawMaterial,
  SemiFinishedProduct,
  TradingGood,
} from '../types'
import { SearchableComponentSelect, SelectableComponentOption } from './SearchableComponentSelect'

interface Props {
  demoSemiFinished: DemoSemiFinishedProduct[]
  setDemoSemiFinished?: React.Dispatch<React.SetStateAction<DemoSemiFinishedProduct[]>>
  setSemiFinished?: React.Dispatch<React.SetStateAction<SemiFinishedProduct[]>>
  demoProducts: DemoProduct[]
  products: Product[]
  categories: Category[]
  rawMaterials: RawMaterial[]
  packagings: Packaging[]
  tradingGoods?: TradingGood[]
  semiFinished: SemiFinishedProduct[]
  onRefresh: () => Promise<void> | void
  onOpenReport?: (item: any, type: string) => void
}

function PieChartIcon({ size = 13, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function PencilIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

export function DemoSemiFinishedView({
  demoSemiFinished,
  setDemoSemiFinished,
  setSemiFinished,
  demoProducts,
  products,
  categories,
  rawMaterials,
  packagings,
  tradingGoods = [],
  semiFinished,
  onRefresh,
  onOpenReport,
}: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingDemoSemiFinished, setEditingDemoSemiFinished] = useState<DemoSemiFinishedProduct | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

  // Component management modal
  const [activeItemForAddComponent, setActiveItemForAddComponent] = useState<DemoSemiFinishedProduct | null>(null)
  const [editingCompQuantity, setEditingCompQuantity] = useState<{
    demoSemiFinished: DemoSemiFinishedProduct
    component: DemoComponentItem
    quantity: string
  } | null>(null)
  const [selectedCompType, setSelectedCompType] = useState<DemoComponentType>('raw_material')
  const [selectedCompId, setSelectedCompId] = useState('')
  const [compAmount, setCompAmount] = useState('1.0000')
  const [compUnitMode, setCompUnitMode] = useState<'kg' | 'g'>('kg')

  // "Gerçek Ortama Geçir" modal state
  const [convertingItem, setConvertingItem] = useState<DemoSemiFinishedProduct | null>(null)
  const [convertCode, setConvertCode] = useState('')
  const [convertError, setConvertError] = useState<string | null>(null)
  const [convertLoading, setConvertLoading] = useState(false)
  const [showOverwritePrompt, setShowOverwritePrompt] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [localHiddenItemIds, setLocalHiddenItemIds] = useState<Set<string>>(new Set())
  const [localHiddenComponentIds, setLocalHiddenComponentIds] = useState<Set<string>>(new Set())

  // Lazy-loaded components cache
  const [lazyComponents, setLazyComponents] = useState<Record<string, DemoComponentItem[]>>({})

  // Maps for fast lookups
  const demoSemiFinishedMap = useMemo(() => {
    return new Map(
      demoSemiFinished.map((s) => {
        if (lazyComponents[s.id]) {
          return [s.id, { ...s, components: lazyComponents[s.id] }]
        }
        return [s.id, s]
      })
    )
  }, [demoSemiFinished, lazyComponents])
  const demoProductsMap = useMemo(() => new Map(demoProducts.map((p) => [p.id, p])), [demoProducts])
  const productsMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])
  const semiFinishedMap = useMemo(() => new Map(semiFinished.map((s) => [s.id, s])), [semiFinished])
  const rawMaterialsMap = useMemo(() => new Map(rawMaterials.map((r) => [r.id, r])), [rawMaterials])
  const packagingsMap = useMemo(() => new Map(packagings.map((p) => [p.id, p])), [packagings])
  const tradingGoodsMap = useMemo(() => new Map((tradingGoods || []).map((t) => [t.id, t])), [tradingGoods])

  // Filtered list
  const filteredItems = useMemo(() => {
    return demoSemiFinished.filter((ds) => {
      if (localHiddenItemIds.has(ds.id)) return false
      const matchSearch = ds.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchCat = !selectedCategory || ds.category_id === selectedCategory
      return matchSearch && matchCat
    })
  }, [demoSemiFinished, searchTerm, selectedCategory, localHiddenItemIds])

  const toggleExpand = async (key: string, ds?: DemoSemiFinishedProduct) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

    if (
      ds &&
      (!ds.components || ds.components.length === 0) &&
      (ds.components_count ?? 0) > 0 &&
      !lazyComponents[ds.id]
    ) {
      try {
        const full = await api.getDemoSemiFinished(ds.id)
        if (full && full.components) {
          setLazyComponents((prev) => ({ ...prev, [ds.id]: full.components || [] }))
        }
      } catch (err) {
        console.error('Failed to load components for demo semi-finished:', err)
      }
    }
  }

  // Handle Promotion to Production ("Gerçek Ortama Geçir")
  const handleOpenConvertModal = (ds: DemoSemiFinishedProduct) => {
    setConvertingItem(ds)
    setConvertCode('')
    setConvertError(null)
    setShowOverwritePrompt(false)
  }

  const handleCloseConvertModal = () => {
    setConvertingItem(null)
    setConvertCode('')
    setConvertError(null)
    setConvertLoading(false)
    setShowOverwritePrompt(false)
  }

  const executeConvert = async (replaceExisting: boolean) => {
    if (!convertingItem) return
    const cleanCode = convertCode.trim()
    setConvertLoading(true)
    setConvertError(null)
    try {
      const realSf = await api.convertToRealSemiFinished(convertingItem.id, {
        code: cleanCode,
        replace_existing: replaceExisting,
      })
      if (setDemoSemiFinished) {
        setDemoSemiFinished((prev) => prev.filter((ds) => ds.id !== convertingItem.id))
      }
      if (setSemiFinished) {
        const cat = categories.find((c) => c.id === realSf.category_id)
        const sfWithCat = { ...realSf, category: cat || realSf.category }
        setSemiFinished((prev) => {
          if (prev.some((s) => s.id === realSf.id)) {
            return prev.map((s) => (s.id === realSf.id ? sfWithCat : s))
          }
          return [sfWithCat, ...prev]
        })
      }
      handleCloseConvertModal()
      if (!setDemoSemiFinished && !setSemiFinished) {
        await onRefresh()
      }
    } catch (err: any) {
      if (err.message && (err.message.includes('zaten kullanılmaktadır') || err.message.includes('409'))) {
        setShowOverwritePrompt(true)
      } else {
        setConvertError(err.message || 'Yarı mamul gerçek ortama aktarılırken bir hata oluştu.')
      }
    } finally {
      setConvertLoading(false)
    }
  }

  const handleConvertSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!convertingItem) return
    const cleanCode = convertCode.trim()
    if (!cleanCode) {
      setConvertError('Lütfen geçerli bir yarı mamul kodu girin.')
      return
    }

    // Client-side quick check
    const codeExists = semiFinished.some((s) => s.code.toLowerCase() === cleanCode.toLowerCase())
    if (codeExists) {
      setShowOverwritePrompt(true)
      return
    }

    await executeConvert(false)
  }

  // Component options for SearchableComponentSelect
  const componentOptions: SelectableComponentOption[] = useMemo(() => {
    if (selectedCompType === 'demo_semi_finished') {
      return demoSemiFinished
        .filter((ds) => activeItemForAddComponent && ds.id !== activeItemForAddComponent.id)
        .map((ds) => ({
          id: ds.id,
          code: 'DEMO',
          name: ds.name,
          unit: ds.unit,
          maliyet: Number(ds.maliyet || 0),
          typeLabel: 'Demo Yarı Mamul',
        }))
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished.map((s) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        unit: s.unit,
        maliyet: Number(s.maliyet || 0),
        typeLabel: 'Yarı Mamul',
      }))
    } else if (selectedCompType === 'raw_material') {
      return rawMaterials.map((r) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        unit: r.unit,
        maliyet: Number(r.maliyet || 0),
        typeLabel: 'Hammadde',
      }))
    } else if (selectedCompType === 'packaging') {
      return packagings.map((pkg) => ({
        id: pkg.id,
        code: pkg.code,
        name: pkg.name,
        unit: pkg.unit,
        maliyet: Number(pkg.maliyet || 0),
        typeLabel: 'Ambalaj',
      }))
    } else if (selectedCompType === 'product') {
      return products.map((p) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        unit: p.unit,
        maliyet: Number(p.maliyet || 0),
        typeLabel: 'Mamul Ürün',
      }))
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).map((tg) => ({
        id: tg.id,
        code: tg.code,
        name: tg.name,
        unit: tg.unit,
        maliyet: Number(tg.maliyet || 0),
        typeLabel: 'Al-Sat',
      }))
    }
    return []
  }, [selectedCompType, demoSemiFinished, semiFinished, rawMaterials, packagings, products, tradingGoods, activeItemForAddComponent])

  const selectedItemMeta = useMemo(() => {
    if (!selectedCompId) return null
    return componentOptions.find((opt) => opt.id === selectedCompId) || null
  }, [componentOptions, selectedCompId])

  const handleAddComponentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeItemForAddComponent || !selectedCompId) return
    setError(null)

    let parsedAmount = parseFloat(compAmount) || 1
    if (selectedItemMeta?.unit === 'Kg' && compUnitMode === 'g') {
      parsedAmount = parsedAmount / 1000
    }

    const parentN = Number(activeItemForAddComponent.recipe_output_quantity || 1)
    const unitAmt = parsedAmount / parentN

    const payload: any = {
      component_type: selectedCompType,
      unit_amount: unitAmt,
      recipe_amount: parsedAmount,
    }

    if (selectedCompType === 'demo_semi_finished') payload.component_demo_semi_finished_id = selectedCompId
    else if (selectedCompType === 'semi_finished') payload.component_semi_finished_id = selectedCompId
    else if (selectedCompType === 'raw_material') payload.component_raw_material_id = selectedCompId
    else if (selectedCompType === 'packaging') payload.component_packaging_id = selectedCompId
    else if (selectedCompType === 'product') payload.component_product_id = selectedCompId
    else if (selectedCompType === 'trading_good') payload.component_trading_good_id = selectedCompId

    try {
      const newComp = await api.addDemoSemiFinishedComponent(activeItemForAddComponent.id, payload)
      const targetId = activeItemForAddComponent.id
      setSelectedCompId('')
      setCompAmount('1.0000')
      setActiveItemForAddComponent(null)
      setExpandedKeys((prev) => new Set(prev).add(`dsf_${targetId}`))
      if (setDemoSemiFinished) {
        setDemoSemiFinished((prev) =>
          prev.map((ds) => {
            if (ds.id !== targetId) return ds
            return {
              ...ds,
              components: [...(ds.components || []), newComp],
            }
          })
        )
      }
      if (lazyComponents[targetId]) {
        setLazyComponents((prev) => ({
          ...prev,
          [targetId]: [...(prev[targetId] || []), newComp],
        }))
      }
      if (!setDemoSemiFinished) {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSaveComponentQuantity = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCompQuantity) return
    try {
      const parentN = Number(editingCompQuantity.demoSemiFinished.recipe_output_quantity || 1)
      const newRecipeAmt = parseFloat(editingCompQuantity.quantity) || 1.0
      const updatedComp = await api.updateDemoSemiFinishedComponent(
        editingCompQuantity.demoSemiFinished.id,
        editingCompQuantity.component.id,
        {
          recipe_amount: newRecipeAmt,
          unit_amount: newRecipeAmt / parentN,
        }
      )
      const targetId = editingCompQuantity.demoSemiFinished.id
      const compId = editingCompQuantity.component.id
      setEditingCompQuantity(null)
      if (setDemoSemiFinished) {
        setDemoSemiFinished((prev) =>
          prev.map((ds) => {
            if (ds.id !== targetId) return ds
            return {
              ...ds,
              components: (ds.components || []).map((c) =>
                c.id === compId
                  ? {
                      ...c,
                      ...updatedComp,
                      recipe_amount: newRecipeAmt,
                      unit_amount: newRecipeAmt / parentN,
                    }
                  : c
              ),
            }
          })
        )
      }
      if (lazyComponents[targetId]) {
        setLazyComponents((prev) => ({
          ...prev,
          [targetId]: (prev[targetId] || []).map((c) =>
            c.id === compId
              ? {
                  ...c,
                  ...updatedComp,
                  recipe_amount: newRecipeAmt,
                  unit_amount: newRecipeAmt / parentN,
                }
              : c
          ),
        }))
      }
      if (!setDemoSemiFinished) {
        await onRefresh()
      }
    } catch (err: any) {
      alert(err.message)
    }
  }

  const handleDeleteComponent = async (demoSemiFinishedId: string, componentId: string) => {
    if (!confirm('Bu bileşeni kaldırmak istediğinizden emin misiniz?')) return
    const parentItem = demoSemiFinished.find((ds) => ds.id === demoSemiFinishedId)
    const compToDelete = parentItem?.components?.find((c) => c.id === componentId)
    const originalIndex = parentItem?.components?.findIndex((c) => c.id === componentId) ?? -1
    const prevLazyComps = lazyComponents[demoSemiFinishedId]

    if (setDemoSemiFinished) {
      setDemoSemiFinished((prev) =>
        prev.map((ds) => {
          if (ds.id !== demoSemiFinishedId) return ds
          return {
            ...ds,
            components: (ds.components || []).filter((c) => c.id !== componentId),
          }
        })
      )
    }
    if (lazyComponents[demoSemiFinishedId]) {
      setLazyComponents((prev) => ({
        ...prev,
        [demoSemiFinishedId]: (prev[demoSemiFinishedId] || []).filter((c) => c.id !== componentId),
      }))
    }
    setLocalHiddenComponentIds((prev) => new Set(prev).add(componentId))

    try {
      await api.deleteDemoSemiFinishedComponent(demoSemiFinishedId, componentId)
    } catch (err: any) {
      if (setDemoSemiFinished && compToDelete && originalIndex >= 0) {
        setDemoSemiFinished((prev) =>
          prev.map((ds) => {
            if (ds.id !== demoSemiFinishedId) return ds
            const comps = [...(ds.components || [])]
            if (!comps.some((c) => c.id === componentId)) {
              comps.splice(originalIndex, 0, compToDelete)
            }
            return { ...ds, components: comps }
          })
        )
      }
      if (prevLazyComps) {
        setLazyComponents((prev) => ({
          ...prev,
          [demoSemiFinishedId]: prevLazyComps,
        }))
      }
      setLocalHiddenComponentIds((prev) => {
        const next = new Set(prev)
        next.delete(componentId)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenComponentIds((prev) => {
      const next = new Set(prev)
      next.delete(componentId)
      return next
    })
  }

  const handleDeleteDemoSemiFinished = async (id: string) => {
    if (!confirm('Bu demo yarı mamulü silmek istediğinizden emin misiniz?')) return
    const itemToDelete = demoSemiFinished.find((ds) => ds.id === id)
    const originalIndex = demoSemiFinished.findIndex((ds) => ds.id === id)

    if (setDemoSemiFinished) {
      setDemoSemiFinished((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenItemIds((prev) => new Set(prev).add(id))

    try {
      await api.deleteDemoSemiFinished(id)
    } catch (err: any) {
      if (setDemoSemiFinished && itemToDelete) {
        setDemoSemiFinished((prev) => {
          if (prev.some((ds) => ds.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenItemIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenItemIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'demo_semi_finished':
        return <span className="badge" style={{ backgroundColor: '#8b5cf6', color: '#fff' }}>Demo Yarı Mamul</span>
      case 'demo_product':
        return <span className="badge" style={{ backgroundColor: '#6366f1', color: '#fff' }}>Demo Mamul</span>
      case 'raw_material':
        return <span className="badge badge-info">Hammadde</span>
      case 'packaging':
        return <span className="badge badge-warning">Ambalaj</span>
      case 'trading_good':
        return <span className="badge" style={{ backgroundColor: '#6f42c1', color: '#fff' }}>Al-Sat</span>
      case 'semi_finished':
        return <span className="badge badge-primary">Yarı Mamul</span>
      case 'product':
        return <span className="badge badge-success">Mamul Ürün</span>
      default:
        return <span className="badge badge-secondary">{type}</span>
    }
  }

  const getRecursiveUnitCost = useCallback(
    (
      componentType: DemoComponentType,
      componentId: string,
      baseCost: number,
      visited: Set<string> = new Set()
    ): number => {
      if (visited.has(componentId)) {
        return baseCost
      }

      if (componentType === 'raw_material') {
        const rm = rawMaterialsMap.get(componentId)
        return Number(rm?.maliyet ?? baseCost)
      }
      if (componentType === 'packaging') {
        const pkg = packagingsMap.get(componentId)
        return Number(pkg?.maliyet ?? baseCost)
      }
      if (componentType === 'trading_good') {
        const tg = tradingGoodsMap.get(componentId)
        return Number(tg?.maliyet ?? baseCost)
      }

      const nextVisited = new Set(visited)
      nextVisited.add(componentId)

      if (componentType === 'demo_semi_finished') {
        const dsf = demoSemiFinishedMap.get(componentId)
        if (!dsf) return baseCost
        return calculateDemoSemiFinishedTotalCost(
          dsf,
          demoSemiFinishedMap,
          demoProductsMap,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap,
          nextVisited
        )
      }

      if (componentType === 'semi_finished') {
        const semi = semiFinishedMap.get(componentId)
        const subComps = semi?.components || []
        let subCost = 0
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childCost = getRecursiveUnitCost(
            child.component_type as DemoComponentType,
            child.component_id,
            Number(child.birim_maliyet || 0),
            nextVisited
          )
          subCost += childQty * childCost
        }
        return subCost
      }

      if (componentType === 'product') {
        const prod = productsMap.get(componentId)
        const subComps = prod?.components || []
        let subCost = 0
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childCost = getRecursiveUnitCost(
            child.component_type as DemoComponentType,
            child.component_id,
            Number(child.birim_maliyet || 0),
            nextVisited
          )
          subCost += childQty * childCost
        }
        return subCost
      }

      return baseCost
    },
    [demoSemiFinishedMap, demoProductsMap, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap]
  )

  const renderComponentTree = (
    rootItem: DemoSemiFinishedProduct,
    components: DemoComponentItem[],
    depth = 1,
    parentKey = '',
    ancestorIds: Set<string> = new Set()
  ): React.ReactNode => {
    return components
      .filter((comp) => !localHiddenComponentIds.has(comp.id))
      .map((comp) => {
      const childKey = `${parentKey || `dsf_${rootItem.id}`}_${comp.id}`
      const isExpanded = expandedKeys.has(childKey)
      const targetEntityId =
        comp.component_demo_semi_finished_id ||
        comp.component_semi_finished_id ||
        comp.component_product_id ||
        comp.component_raw_material_id ||
        comp.component_packaging_id ||
        ('component_trading_good_id' in comp ? (comp as any).component_trading_good_id : null) ||
        comp.id

      let subComponents: any[] = []
      let hasSub = false
      let resolvedUnit = comp.component_unit || 'Adet'
      let unitCost = 0

      if (comp.component_type === 'demo_semi_finished' && comp.component_demo_semi_finished_id) {
        const dsf = demoSemiFinishedMap.get(comp.component_demo_semi_finished_id)
        if (dsf) {
          resolvedUnit = dsf.unit || 'Adet'
          subComponents = dsf.components || []
          hasSub = subComponents.length > 0
          unitCost = getRecursiveUnitCost(comp.component_type, dsf.id, Number(dsf.maliyet || 0), ancestorIds)
        }
      } else if (comp.component_type === 'semi_finished' && comp.component_semi_finished_id) {
        const sf = semiFinishedMap.get(comp.component_semi_finished_id)
        if (sf) {
          resolvedUnit = sf.unit || 'Adet'
          subComponents = sf.components || []
          hasSub = subComponents.length > 0
          unitCost = getRecursiveUnitCost(comp.component_type, sf.id, 0, ancestorIds)
        }
      } else if (comp.component_type === 'product' && comp.component_product_id) {
        const prod = productsMap.get(comp.component_product_id)
        if (prod) {
          resolvedUnit = prod.unit || 'Adet'
          subComponents = prod.components || []
          hasSub = subComponents.length > 0
          unitCost = getRecursiveUnitCost(comp.component_type, prod.id, 0, ancestorIds)
        }
      } else if (comp.component_type === 'raw_material' && comp.component_raw_material_id) {
        const rm = rawMaterialsMap.get(comp.component_raw_material_id)
        if (rm) {
          resolvedUnit = rm.unit || 'Adet'
          unitCost = Number(rm.maliyet || 0)
        }
      } else if (comp.component_type === 'packaging' && comp.component_packaging_id) {
        const pkg = packagingsMap.get(comp.component_packaging_id)
        if (pkg) {
          resolvedUnit = pkg.unit || 'Adet'
          unitCost = Number(pkg.maliyet || 0)
        }
      } else if (comp.component_type === 'trading_good' && ('component_trading_good_id' in comp ? (comp as any).component_trading_good_id : null)) {
        const tg = tradingGoodsMap.get((comp as any).component_trading_good_id)
        if (tg) {
          resolvedUnit = tg.unit || 'Adet'
          unitCost = Number(tg.maliyet || 0)
        }
      }

      const parentN = Number(rootItem.recipe_output_quantity || 1)
      const unitAmount = Number(comp.unit_amount || 0)
      const recipeAmount = comp.recipe_amount !== undefined && comp.recipe_amount !== null
        ? Number(comp.recipe_amount)
        : unitAmount * parentN
      const totalCompCost = unitAmount * unitCost

      const isCycle = ancestorIds.has(targetEntityId)
      const childPath = `${parentKey || `dsf_${rootItem.id}`}_${comp.id}`

      const isDirectChild = depth === 1
      const isNode = hasSub

      return (
        <div key={comp.id} className="bom-tree-item-wrapper">
          <div
            className={`bom-tree-row bom-tree-depth-${Math.min(depth, 3)}`}
            style={{ paddingLeft: `${(depth - 1) * 24 + 10}px` }}
          >
            <div className="bom-row-left">
              {hasSub && !isCycle ? (
                <button
                  type="button"
                  className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleExpand(childKey)}
                  aria-label={`${comp.component_name || 'Bileşen'} alt bileşenlerini aç/kapa`}
                  aria-expanded={isExpanded}
                >
                  <ChevronIcon size={12} />
                </button>
              ) : (
                <span className="tree-leaf-spacer" aria-hidden="true" />
              )}
              {getTypeBadge(comp.component_type)}
              {comp.component_code && <code className="bom-code-badge">{comp.component_code}</code>}
              <span className="bom-item-name" style={{ fontWeight: isDirectChild ? 600 : 400 }}>
                {comp.component_name || 'Bileşen'}
              </span>
              {(() => {
                let sf: any = null
                let sType = ''
                if (comp.component_type === 'demo_semi_finished') {
                  sf = demoSemiFinishedMap.get(targetEntityId)
                  sType = 'demo_semi_finished'
                } else if (comp.component_type === 'semi_finished') {
                  sf = semiFinishedMap.get(targetEntityId)
                  sType = 'semi_finished'
                }
                if (!sf || !isSemiFinishedEligibleForRecipeReport(sf)) return null
                return (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenReport?.(sf, sType)
                    }}
                    title="Yarı Mamul Reçete Raporu"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 4px',
                      marginLeft: '6px',
                      lineHeight: 1,
                      verticalAlign: 'middle',
                    }}
                  >
                    <PieChartIcon size={12} style={{ marginRight: 0 }} />
                  </button>
                )
              })()}
              {isCycle && (
                <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>
                  Döngüsel Referans
                </span>
              )}
            </div>

            <div className="bom-row-right">
              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Miktar:</span>
                <span className="tabular-nums bom-pill-value">
                  {formatQuantity(unitAmount)} {resolvedUnit}
                </span>
                {isDirectChild && (
                  <button
                    type="button"
                    className="cell-edit-btn"
                    aria-label={`${comp.component_name || 'Bileşen'} miktarını düzenle`}
                    onClick={() =>
                      setEditingCompQuantity({
                        demoSemiFinished: rootItem,
                        component: comp,
                        quantity: String(recipeAmount),
                      })
                    }
                  >
                    <PencilIcon size={11} />
                  </button>
                )}
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">
                  {isNode ? 'Taban Maliyet:' : 'Birim Maliyet:'}
                </span>
                <span className="tabular-nums bom-pill-value">₺{unitCost.toFixed(2)}</span>
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Toplam Maliyet:</span>
                <span
                  className="tabular-nums bom-pill-value"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                >
                  ₺{totalCompCost.toFixed(2)}
                </span>
              </div>

              <div className="bom-row-actions">
                {isDirectChild ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                    aria-label={`${comp.component_name || 'Bileşen'} bileşenini kaldır`}
                    onClick={() => handleDeleteComponent(rootItem.id, comp.id)}
                  >
                    ✕ Kaldır
                  </button>
                ) : (
                  <span
                    className="text-muted"
                    style={{ fontSize: '0.75rem', width: '50px', textAlign: 'center', display: 'inline-block' }}
                    title="Alt bileşenlerin reçetesi kendi menüsünden yönetilir"
                  >
                    —
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Sub-components */}
          {hasSub && isExpanded && (
            <div className="bom-sub-tree">
              {isCycle ? (
                <div
                  className="bom-tree-alert"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  ⚠️ Döngüsel referans algılandı ({comp.component_name || 'Bileşen'})
                </div>
              ) : subComponents.length === 0 ? (
                <div
                  className="bom-tree-empty-hint"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  Bu alt bileşenin tanımlı bir reçetesi bulunmuyor.
                </div>
              ) : (
                renderComponentTree(
                  rootItem,
                  subComponents,
                  depth + 1,
                  childPath,
                  new Set([...ancestorIds, targetEntityId])
                )
              )}
            </div>
          )}
        </div>
      )
    })
  }

  // Check if convertingItem has child demo items
  const hasSubDemoComponents = useMemo(() => {
    if (!convertingItem || !convertingItem.components) return false
    return convertingItem.components.some(
      (c) => c.component_type === 'demo_product' || c.component_type === 'demo_semi_finished'
    )
  }, [convertingItem])

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Demo Yarı Mamuller</h2>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          + Yeni Demo Yarı Mamul Ekle
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Demo yarı mamul ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '280px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: '38px' }}></th>
              <th>Demo Yarı Mamul Adı</th>
              <th>Kategori</th>
              <th>Birim</th>
              <th style={{ textAlign: 'right' }}>Reçete Çıktısı (n)</th>
              <th style={{ textAlign: 'right' }}>Taban Maliyet</th>
              <th style={{ textAlign: 'right' }}>Bileşenler</th>
              <th style={{ textAlign: 'right' }}>Toplam Maliyet</th>
              <th style={{ width: '210px', textAlign: 'center' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center empty-state" style={{ padding: '3rem 1rem' }}>
                  <div style={{ color: 'var(--text-muted, #6b7280)', marginBottom: '0.75rem' }}>
                    Henüz tanımlı bir demo yarı mamul bulunmamaktadır.
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => setShowAddModal(true)}>
                    + İlk Demo Yarı Mamulü Ekle
                  </button>
                </td>
              </tr>
            ) : (
              filteredItems.map((ds) => {
                const rootKey = `dsf_${ds.id}`
                const isExpanded = expandedKeys.has(rootKey)
                const comps = (lazyComponents[ds.id] || ds.components || []).filter((c) => !localHiddenComponentIds.has(c.id))
                const totalCost = calculateDemoSemiFinishedTotalCost(
                  ds,
                  demoSemiFinishedMap,
                  demoProductsMap,
                  productsMap,
                  semiFinishedMap,
                  rawMaterialsMap,
                  packagingsMap,
                  tradingGoodsMap
                )
                const baseCost = Number(ds.maliyet || 0)
                const componentsCost = Math.max(0, totalCost - baseCost)

                return (
                  <Fragment key={ds.id}>
                    <tr style={{ height: '76px' }}>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <button
                          type="button"
                          className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                          aria-label={
                            isExpanded
                              ? `${ds.name} bileşen ağacını daralt`
                              : `${ds.name} bileşen ağacını genişlet`
                          }
                          aria-expanded={isExpanded}
                          onClick={() => toggleExpand(rootKey, ds)}
                        >
                          <ChevronIcon />
                        </button>
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <strong>{ds.name}</strong>
                      </td>
                      <td className="text-muted" style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        {ds.category_name || categories.find((c) => c.id === ds.category_id)?.name || '—'}
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>{ds.unit || 'Adet'}</td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {formatQuantity(ds.recipe_output_quantity || 1)} {ds.unit || 'Adet'}
                      </td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {baseCost.toFixed(2)} TL
                      </td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem' }} className="tabular-nums">
                        {componentsCost > 0 ? `${componentsCost.toFixed(2)} TL` : '—'}
                      </td>
                      <td style={{ textAlign: 'right', verticalAlign: 'middle', padding: '0.75rem 0.5rem', fontWeight: 600 }} className="tabular-nums">
                        ₺{totalCost.toFixed(2)}
                      </td>
                      <td style={{ verticalAlign: 'middle', padding: '0.75rem 0.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxWidth: '200px', margin: '0 auto' }}>
                          <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
                            {isSemiFinishedEligibleForRecipeReport(ds) && (
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => onOpenReport?.(ds, 'demo_semi_finished')}
                                title="Reçete Raporu"
                              >
                                <PieChartIcon size={14} style={{ marginRight: 0 }} />
                              </button>
                            )}

                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem', flex: 1, whiteSpace: 'nowrap' }}
                              onClick={() => setActiveItemForAddComponent(ds)}
                              title="Yeni Bileşen Ekle"
                            >
                              + Bileşen
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem', flex: 1, whiteSpace: 'nowrap' }}
                              onClick={() => setEditingDemoSemiFinished(ds)}
                              title="Düzenle"
                            >
                              Düzenle
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.45rem' }}
                              onClick={() => handleDeleteDemoSemiFinished(ds.id)}
                              title="Sil"
                            >
                              Sil
                            </button>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.75rem', padding: '0.3rem 0.5rem', width: '100%', whiteSpace: 'nowrap' }}
                            onClick={() => handleOpenConvertModal(ds)}
                            title="Bu demo yarı mamulü gerçek üretim kataloğuna aktar"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                            <span>Gerçek Ortama Geçir</span>
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Cascading Tree Expanded */}
                    {isExpanded && (
                      <tr className="bom-expansion-row">
                        <td colSpan={9} className="bom-expansion-cell">
                          <div className="bom-dropdown-container">
                            {comps.length === 0 ? (
                              <div className="bom-empty-state">
                                <span>Bu demo yarı mamule henüz bir bileşen eklenmemiş.</span>
                                <button
                                  type="button"
                                  className="tree-add-btn"
                                  onClick={() => setActiveItemForAddComponent(ds)}
                                >
                                  + İlk Bileşeni Ekle
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="bom-dropdown-header">
                                  <span className="bom-dropdown-title">
                                    {ds.name} — Reçete Ağacı ({comps.length} Doğrudan Bileşen)
                                  </span>
                                  <button
                                    type="button"
                                    className="tree-add-btn"
                                    onClick={() => setActiveItemForAddComponent(ds)}
                                  >
                                    + Yeni Bileşen Ekle
                                  </button>
                                </div>
                                <div className="bom-dropdown-body">
                                  {renderComponentTree(ds, comps, 1, `dsf_${ds.id}`, new Set([ds.id]))}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: Gerçek Ortama Geçir */}
      {convertingItem && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3>Gerçek Ortama Geçir</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              <strong>{convertingItem.name}</strong> adlı demo yarı mamul gerçek üretim kataloğuna aktarılacaktır.
            </p>

            {hasSubDemoComponents && (
              <div className="alert alert-danger" style={{ marginBottom: '1.25rem', fontSize: '0.85rem' }}>
                <strong>Dikkat:</strong> Bu demo yarı mamulün alt bileşenleri arasında başka bir demo mamul veya demo yarı mamul bulunmaktadır. Gerçek yarı mamuller demo öğe içeremez. Lütfen önce alt demo öğesini gerçek ortama aktarın veya bileşenlerden çıkarın.
              </div>
            )}

            {convertError && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{convertError}</div>}

            {showOverwritePrompt ? (
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                <h4 style={{ color: '#b45309', margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Kod Zaten Kullanımda</h4>
                <p style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '1rem' }}>
                  <strong>'{convertCode}'</strong> yarı mamul kodu zaten kullanılmaktadır. Mevcut kaydın üzerine yazmak istiyor musunuz?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowOverwritePrompt(false)}
                    disabled={convertLoading}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => executeConvert(true)}
                    disabled={convertLoading}
                  >
                    {convertLoading ? 'İşleniyor…' : 'Evet, Üzerine Yaz'}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleConvertSubmit}>
                <div className="form-group">
                  <label>Yeni Yarı Mamul Kodu *</label>
                  <input
                    required
                    autoFocus
                    placeholder="Örn: YM-1001, YM-GOVDE-01"
                    value={convertCode}
                    onChange={(e) => setConvertCode(e.target.value)}
                    disabled={hasSubDemoComponents || convertLoading}
                    className="form-control"
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseConvertModal}
                    disabled={convertLoading}
                  >
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={hasSubDemoComponents || convertLoading}
                  >
                    {convertLoading ? 'Aktarılıyor…' : 'Gerçek Ortama Aktar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal: Yeni Demo Yarı Mamul Ekle */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '520px', width: '90%' }}>
            <h3>Yeni Demo Yarı Mamul Ekle</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                const form = e.currentTarget
                const data = new FormData(form)
                try {
                  const created = await api.createDemoSemiFinished({
                    name: String(data.get('name') || ''),
                    description: String(data.get('description') || '') || null,
                    unit: String(data.get('unit') || 'Adet'),
                    recipe_output_quantity: Number(data.get('recipe_output_quantity')) || 1,
                    category_id: String(data.get('category_id') || '') || null,
                  })
                  setShowAddModal(false)
                  if (setDemoSemiFinished) {
                    setDemoSemiFinished((prev) => [created, ...prev])
                  } else {
                    await onRefresh()
                  }
                } catch (err: any) {
                  alert(err.message)
                }
              }}
            >
              <div className="form-group">
                <label>Demo Yarı Mamul Adı *</label>
                <input required autoFocus name="name" className="form-control" placeholder="Örn: Prototip Gövde Modülü" />
              </div>
              <div className="form-group">
                <label>Kategori</label>
                <select name="category_id" className="form-control">
                  <option value="">Seçiniz...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Birim *</label>
                <select name="unit" className="form-control" defaultValue="Adet">
                  <option value="Adet">Adet</option>
                  <option value="Kg">Kg</option>
                  <option value="Lt">Lt</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reçete Çıktısı (n) *</label>
                <input
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  required
                  name="recipe_output_quantity"
                  defaultValue="1"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Açıklama</label>
                <textarea name="description" className="form-control" rows={2} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Vazgeç
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Demo Yarı Mamulü Düzenle */}
      {editingDemoSemiFinished && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '520px', width: '90%' }}>
            <h3>Demo Yarı Mamulü Düzenle</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                const form = e.currentTarget
                const data = new FormData(form)
                try {
                  const updated = await api.updateDemoSemiFinished(editingDemoSemiFinished.id, {
                    name: String(data.get('name') || ''),
                    description: String(data.get('description') || '') || null,
                    unit: String(data.get('unit') || 'Adet'),
                    recipe_output_quantity: Number(data.get('recipe_output_quantity')) || 1,
                    category_id: String(data.get('category_id') || '') || null,
                  })
                  setEditingDemoSemiFinished(null)
                  if (setDemoSemiFinished) {
                    setDemoSemiFinished((prev) =>
                      prev.map((ds) => (ds.id === updated.id ? { ...ds, ...updated } : ds))
                    )
                  } else {
                    await onRefresh()
                  }
                } catch (err: any) {
                  alert(err.message)
                }
              }}
            >
              <div className="form-group">
                <label>Demo Yarı Mamul Adı *</label>
                <input
                  required
                  name="name"
                  defaultValue={editingDemoSemiFinished.name}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Kategori</label>
                <select
                  name="category_id"
                  className="form-control"
                  defaultValue={editingDemoSemiFinished.category_id || ''}
                >
                  <option value="">Seçiniz...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Birim *</label>
                <select name="unit" className="form-control" defaultValue={editingDemoSemiFinished.unit || 'Adet'}>
                  <option value="Adet">Adet</option>
                  <option value="Kg">Kg</option>
                  <option value="Lt">Lt</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reçete Çıktısı (n) *</label>
                <input
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  required
                  name="recipe_output_quantity"
                  defaultValue={Number(editingDemoSemiFinished.recipe_output_quantity || 1)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows={2}
                  defaultValue={editingDemoSemiFinished.description || ''}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingDemoSemiFinished(null)}
                >
                  Vazgeç
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Yeni Bileşen Ekle */}
      {activeItemForAddComponent && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '520px', width: '90%' }}>
            <h3>Bileşen Ekle: {activeItemForAddComponent.name}</h3>
            <form onSubmit={handleAddComponentSubmit}>
              <div className="form-group">
                <label>Bileşen Türü *</label>
                <select
                  className="form-control"
                  value={selectedCompType}
                  onChange={(e) => {
                    setSelectedCompType(e.target.value as DemoComponentType)
                    setSelectedCompId('')
                  }}
                >
                  <option value="raw_material">Hammadde</option>
                  <option value="packaging">Ambalaj</option>
                  <option value="trading_good">Al-Sat Ürün</option>
                  <option value="semi_finished">Yarı Mamul (Gerçek)</option>
                  <option value="demo_semi_finished">Demo Yarı Mamul</option>
                  <option value="product">Mamul Ürün (Gerçek)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Bileşen Seçimi *</label>
                <SearchableComponentSelect
                  options={componentOptions}
                  value={selectedCompId}
                  onChange={(val) => setSelectedCompId(val)}
                  placeholder="Bileşen arayın veya seçin..."
                />
              </div>

              {selectedItemMeta && (
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label>
                      {activeItemForAddComponent.recipe_output_quantity || 1} {activeItemForAddComponent.unit} Başına Miktar *
                    </label>
                    {selectedItemMeta.unit === 'Kg' && (
                      <div className="btn-group">
                        <button
                          type="button"
                          className={`btn btn-xs ${compUnitMode === 'kg' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setCompUnitMode('kg')}
                        >
                          Kg
                        </button>
                        <button
                          type="button"
                          className={`btn btn-xs ${compUnitMode === 'g' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => setCompUnitMode('g')}
                        >
                          g
                        </button>
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    required
                    className="form-control"
                    value={compAmount}
                    onChange={(e) => setCompAmount(e.target.value)}
                  />
                  <small className="text-muted">
                    Birim: {compUnitMode === 'g' && selectedItemMeta.unit === 'Kg' ? 'Gram (g)' : selectedItemMeta.unit}
                  </small>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveItemForAddComponent(null)}
                >
                  Vazgeç
                </button>
                <button type="submit" className="btn btn-primary" disabled={!selectedCompId}>
                  Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Miktar Düzenle */}
      {editingCompQuantity && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: '440px', width: '90%' }}>
            <h3>Bileşen Miktarını Düzenle</h3>
            <form onSubmit={handleSaveComponentQuantity}>
              <div className="form-group">
                <label>
                  {editingCompQuantity.demoSemiFinished.recipe_output_quantity || 1} {editingCompQuantity.demoSemiFinished.unit} Başına Miktar *
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="0.000001"
                  required
                  autoFocus
                  className="form-control"
                  value={editingCompQuantity.quantity}
                  onChange={(e) =>
                    setEditingCompQuantity({
                      ...editingCompQuantity,
                      quantity: e.target.value,
                    })
                  }
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingCompQuantity(null)}
                >
                  Vazgeç
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
